import { v4 as uuidv4 } from "uuid";

// ---------------------------------------------------------------------------
// Faith & Spirituality personalization — schema-driven helpers.
// The wizard is rendered from the server schema (GET /ARIS/v2/faith-profile/
// schema); these helpers translate between the schema + member answers and the
// FaithProfileInput save body. See docs/faith_frontend_integration_plan.md.
// Nothing here hardcodes the questions/options — they all come from the schema.
// ---------------------------------------------------------------------------

// Step types we know how to render. Unknown types are skipped (forward-compat).
export const KNOWN_STEP_TYPES = [
  "info",
  "single_select",
  "multi_select",
  "boolean",
  "schedule_builder",
];

// Answer key for a step: direct steps target a FaithProfileInput `field`;
// role/fan-out steps (practice_selector, schedule_builder) key by `id`.
export const stepKey = (step) => step.field || step.id;

// Convert a 12h label like "7:00 AM" to 24h "HH:MM"; pass through "HH:MM".
export const to24Hour = (label) => {
  if (!label) return "";
  const match = String(label).trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (!match) return label;
  let [, hh, mm, meridiem] = match;
  let hour = parseInt(hh, 10) % 12;
  if (meridiem.toLowerCase() === "pm") hour += 12;
  return `${String(hour).padStart(2, "0")}:${mm}`;
};

// Display "HH:MM" (24h) as "7:00 AM".
export const to12Hour = (time) => {
  if (!time) return "";
  const [hStr, mStr] = String(time).split(":");
  let hour = parseInt(hStr, 10);
  if (Number.isNaN(hour)) return time;
  const meridiem = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${mStr ?? "00"} ${meridiem}`;
};

// ---- schema introspection ----

export const orderedSteps = (schema) =>
  [...(schema?.steps || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

// A step shows when it has no visible_if, or answers[field] === equals.
export const isStepVisible = (step, answers) => {
  const cond = step?.visible_if;
  if (!cond) return true;
  return answers?.[cond.field] === cond.equals;
};

// Ordered list of steps currently visible given the answers. Drives "Step X of N".
export const visibleSteps = (schema, answers) =>
  orderedSteps(schema)
    .filter((s) => KNOWN_STEP_TYPES.includes(s.type))
    .filter((s) => isStepVisible(s, answers));

// The practice-selector step (role: practice_selector) and the schedule builder.
export const findPracticeSelector = (schema) =>
  orderedSteps(schema).find((s) => s.role === "practice_selector");
export const findScheduleBuilder = (schema) =>
  orderedSteps(schema).find((s) => s.type === "schedule_builder");

// value -> { label, practice_type } from the practice-selector options.
export const buildPracticeLookup = (schema) => {
  const selector = findPracticeSelector(schema);
  const lookup = {};
  (selector?.options || []).forEach((opt) => {
    lookup[opt.value] = { label: opt.label, practice_type: opt.practice_type || "custom" };
  });
  return lookup;
};

// value -> label resolver across all single/multi select steps (for review/summary).
export const buildLabelResolver = (schema) => {
  const map = {};
  orderedSteps(schema).forEach((step) => {
    const field = step.field;
    if (!field || !step.options) return;
    map[field] = map[field] || {};
    step.options.forEach((opt) => {
      map[field][String(opt.value)] = opt.label;
    });
  });
  return (field, value) => map[field]?.[String(value)];
};

// ---- answers <-> profile ----

// First-time defaults: booleans answered as false (a valid "No"); selects left
// empty so required steps force a choice; role steps start empty.
export const defaultAnswers = (schema) => {
  const answers = {};
  orderedSteps(schema).forEach((step) => {
    if (step.type === "boolean" && step.field) answers[step.field] = false;
    else if (step.type === "single_select" && step.field) answers[step.field] = "";
    else if (step.type === "multi_select" && step.field) answers[step.field] = [];
    else if (step.role === "practice_selector") answers[stepKey(step)] = [];
    else if (step.type === "schedule_builder") answers[stepKey(step)] = {};
  });
  answers.reflection_preferences = [];
  return answers;
};

// Rebuild answers from a saved profile for prefill/resume. Groups prayer_schedules
// by name → selected practices + per-practice times; keeps existing entry_ids.
export const parseAnswersFromProfile = (schema, profile) => {
  const answers = defaultAnswers(schema);
  if (!profile) return { answers, existingEntries: [] };

  orderedSteps(schema).forEach((step) => {
    if (!step.field) return;
    if (profile[step.field] !== undefined && profile[step.field] !== null) {
      answers[step.field] = profile[step.field];
    }
  });

  const selector = findPracticeSelector(schema);
  const builder = findScheduleBuilder(schema);
  const schedules = profile.prayer_schedules || [];
  if (selector && builder && schedules.length) {
    // label -> option.value (match stored name to a current option)
    const labelToValue = {};
    (selector.options || []).forEach((opt) => {
      labelToValue[opt.label] = opt.value;
    });
    const practices = [];
    const times = {};
    schedules.forEach((entry) => {
      const value = labelToValue[entry.name];
      if (!value) return; // option no longer in schema — skip
      if (!practices.includes(value)) practices.push(value);
      if (!times[value]) times[value] = [];
      if (entry.time_local && !times[value].includes(entry.time_local)) {
        times[value].push(entry.time_local);
      }
    });
    answers[stepKey(selector)] = practices;
    answers[stepKey(builder)] = times;
  }

  return {
    answers,
    existingEntries: schedules.map((e) => ({
      entry_id: e.entry_id,
      name: e.name,
      time_local: e.time_local,
    })),
  };
};

// Fan-out: one entry per (practice × time). Reuse entry_id for an existing
// (name, time_local) so the server updates rather than duplicates.
export const buildPrayerSchedules = (practiceValues, timesByValue, practiceLookup, existingEntries = []) => {
  const schedules = [];
  (practiceValues || []).forEach((value) => {
    const meta = practiceLookup[value];
    if (!meta) return;
    const times = timesByValue?.[value] || [];
    times.forEach((rawTime) => {
      const time_local = to24Hour(rawTime);
      const existing = existingEntries.find((e) => e.name === meta.label && e.time_local === time_local);
      schedules.push({
        entry_id: existing?.entry_id || uuidv4(),
        name: meta.label,
        practice_type: meta.practice_type,
        time_local,
        days_of_week: null,
      });
    });
  });
  return schedules;
};

// Assemble the FaithProfileInput PUT body from schema + answers.
// Hidden steps are cleared (hidden booleans send false); prayer_schedules is the
// fan-out, or [] when prayer support is off.
export const assembleProfileBody = (schema, answers, { faithTradition, existingEntries = [] } = {}) => {
  const body = { reflection_preferences: [] };

  orderedSteps(schema).forEach((step) => {
    if (!step.field) return;
    const visible = isStepVisible(step, answers);
    if (!visible) {
      if (step.type === "boolean") body[step.field] = false;
      return;
    }
    if (step.type === "boolean") body[step.field] = Boolean(answers[step.field]);
    else if (step.type === "single_select") body[step.field] = answers[step.field] || null;
    else if (step.type === "multi_select") body[step.field] = answers[step.field] || [];
  });

  if (faithTradition) body.faith_tradition = faithTradition;

  const selector = findPracticeSelector(schema);
  const builder = findScheduleBuilder(schema);
  const prayerOn = !!body.prayer_support_enabled;
  if (prayerOn && selector && builder && isStepVisible(selector, answers)) {
    body.prayer_schedules = buildPrayerSchedules(
      answers[stepKey(selector)],
      answers[stepKey(builder)],
      buildPracticeLookup(schema),
      existingEntries,
    );
  } else {
    body.prayer_schedules = [];
  }

  return body;
};

// ---- review / summary rows ----

// Resolve one review row against a profile-shaped object. Returns { label, lines }.
export const resolveReviewRow = (row, profileLike, resolveLabel) => {
  const { label, source, as } = row;
  const raw = profileLike?.[source];

  if (as === "enabled_disabled") {
    return { label, lines: [raw ? "Enabled" : "Disabled"] };
  }

  if (as === "practice_names") {
    const schedules = Array.isArray(raw) ? raw : profileLike?.prayer_schedules || [];
    const names = [];
    schedules.forEach((e) => {
      if (e?.name && !names.includes(e.name)) names.push(e.name);
    });
    return { label, lines: names.length ? names : ["None"] };
  }

  if (as === "practice_times") {
    const schedules = Array.isArray(raw) ? raw : profileLike?.prayer_schedules || [];
    const byName = {};
    const order = [];
    schedules.forEach((e) => {
      if (!e?.name) return;
      if (!byName[e.name]) {
        byName[e.name] = [];
        order.push(e.name);
      }
      if (e.time_local) byName[e.name].push(to12Hour(e.time_local));
    });
    const lines = order.map((name) => `${name} - ${byName[name].join(", ")}`);
    return { label, lines: lines.length ? lines : ["None"] };
  }

  // No transform: map enum -> label when possible, else show the raw value.
  const mapped = (resolveLabel && resolveLabel(source, raw)) || raw;
  return { label, lines: [mapped != null && mapped !== "" ? String(mapped) : "—"] };
};
